import React, { useEffect, useRef, useState } from "react";
import { MapPin, Navigation, Loader2 } from "lucide-react";
import { loadMaps } from "../lib/gmaps";

const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0a0a0a" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0a0a0a" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8a867e" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#a8a49a" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1a1a1a" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#2a2a2a" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#6a6862" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#050505" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4a4a4a" }] },
];

export const RouteMap = ({ origin, destination, height = 320 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const rendererRef = useRef(null);
  const libsRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await loadMaps();
        if (!containerRef.current || !window.google?.maps?.Map) throw new Error("maps not ready");
        mapRef.current = new window.google.maps.Map(containerRef.current, {
          center: { lat: 46.94, lng: 7.90 },
          zoom: 7,
          disableDefaultUI: true,
          gestureHandling: "cooperative",
          styles: DARK_STYLE,
          backgroundColor: "#0a0a0a",
        });
        rendererRef.current = new window.google.maps.DirectionsRenderer({
          map: mapRef.current,
          suppressMarkers: true,
          polylineOptions: { strokeColor: "#e8e4da", strokeWeight: 3, strokeOpacity: 0.85 },
        });
        setReady(true);
      } catch (err) {
        console.error("[RouteMap] load failed:", err?.message);
        setError(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!mapRef.current || !ready || error || !window.google?.maps) return;
    // Clear old markers
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    rendererRef.current?.setDirections({ routes: [] });

    const points = [origin, destination].filter((p) => p && (p.lat || p.address));
    if (points.length === 0) return;

    const geo = new window.google.maps.Geocoder();
    const resolvePt = (p) => {
      if (!p) return Promise.resolve(null);
      if (p.lat && p.lng) return Promise.resolve({ lat: p.lat, lng: p.lng });
      if (p.address) return new Promise((res) => geo.geocode({ address: p.address }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          res({ lat: loc.lat(), lng: loc.lng() });
        } else res(null);
      }));
      return Promise.resolve(null);
    };

    Promise.all([resolvePt(origin), resolvePt(destination)]).then(([a, b]) => {
      const bounds = new window.google.maps.LatLngBounds();
      if (a) {
        const m = new window.google.maps.Marker({
          map: mapRef.current,
          position: a,
          icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#e8e4da", fillOpacity: 1, strokeColor: "#0a0a0a", strokeWeight: 2 },
        });
        markersRef.current.push(m); bounds.extend(a);
      }
      if (b) {
        const m = new window.google.maps.Marker({
          map: mapRef.current,
          position: b,
          icon: { path: "M -8 0 L 0 -10 L 8 0 L 0 4 Z", scale: 1.4, fillColor: "#e8e4da", fillOpacity: 1, strokeColor: "#0a0a0a", strokeWeight: 1.5 },
        });
        markersRef.current.push(m); bounds.extend(b);
      }
      if (a && b) {
        const dr = new window.google.maps.DirectionsService();
        dr.route({ origin: a, destination: b, travelMode: window.google.maps.TravelMode.DRIVING }, (res, status) => {
          if (status === "OK") rendererRef.current.setDirections(res);
        });
        mapRef.current.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      } else if (a || b) {
        mapRef.current.setCenter(a || b);
        mapRef.current.setZoom(11);
      }
    });
  }, [origin, destination, ready, error]);

  if (error) {
    return (
      <div className="border border-brand-border p-6 text-center text-brand-silver text-sm" style={{ height }}>
        <MapPin className="mx-auto mb-2" size={20} />
        Map unavailable. Google Maps API key not configured.
      </div>
    );
  }

  return (
    <div ref={containerRef} data-testid="route-map" className="w-full border border-brand-border" style={{ height }} />
  );
};

export const CurrentLocationButton = ({ onLocation, label = "Use current location", className = "" }) => {
  const [loading, setLoading] = useState(false);
  const get = () => {
    if (!navigator.geolocation) return;
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        let label = `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`;
        try {
          if (window.google?.maps?.Geocoder) {
            const geo = new window.google.maps.Geocoder();
            await new Promise((res) => geo.geocode({ location: coords }, (r, status) => {
              if (status === "OK" && r[0]) label = r[0].formatted_address;
              res();
            }));
          }
        } catch (err) {
          // Reverse geocode failed — we still return the coords label.
          console.warn("[CurrentLocationButton] Reverse geocode failed:", err?.message);
        }
        onLocation(label, coords);
        setLoading(false);
      },
      () => setLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  return (
    <button
      type="button"
      onClick={get}
      data-testid="use-current-location"
      className={`inline-flex items-center gap-2 text-[10px] tracking-widest2 uppercase text-brand-silver hover:text-brand-platinum transition-colors ${className}`}
    >
      {loading ? <Loader2 size={12} className="animate-spin" /> : <Navigation size={12} />}
      {label}
    </button>
  );
};

export default RouteMap;
